import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { sessionAPI, campaignAPI } from "../../services/api";
import { useNavigate } from "react-router-dom";
import {
  getSessionRecommendation,
} from "../../utils/hiringRecommendation";
import { USE_DEMO_DATA } from "../../data/demoMode";
import {
  DUMMY_SESSIONS,
  buildRecruiterOptions,
  withCurrentRecruiterInDemoSessions,
} from "../../data/dashboardDummyData";
import { DUMMY_CAMPAIGNS } from "../../data/campaignsDummyData";

const C = {
  brand: "#6366F1",
  blue: "#3B82F6",
  green: "#10B981",
  purple: "#8B5CF6",
  red: "#EF4444",
  t1: "#1E293B",
  t2: "#475569",
  t3: "#94A3B8",
  t4: "#94A3B8",
  greenLight: "#ECFDF5",
};

/** Thin-stroke KPI glyphs (match dashboard / reference cards). */
function AnalyticsKpiIcon({ color, children }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="17"
      height="17"
      aria-hidden
    >
      {children}
    </svg>
  );
}

function AnimBar({ value, color, delay = 0, height = 6 }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(value), 100 + delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return (
    <div style={{ flex: 1, width: "100%", height, background: "#F3F4F6", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${w}%`, background: color, borderRadius: 3, transition: "width 1s ease" }} />
    </div>
  );
}

const MATRIX_T = {
  card: "#fff",
  bdr: "#E2E8F0",
  bdr2: "#CBD5E1",
  t0: "#0F172A",
  t1: "#1E293B",
  t2: "#475569",
  t3: "#94A3B8",
  t4: "#94A3B8",
  green: "#10B981",
  red: "#EF4444",
  amber: "#F59E0B",
  greenLight: "#D1FAE5",
  redLight: "#FEE2E2",
  amberLight: "#FEF3C7",
};

const MATRIX_BADGE = {
  strong_yes: { label: "Strong Hire", color: "#065F46", bg: "#D1FAE5" },
  yes: { label: "Hire", color: "#92400E", bg: "#FEF3C7" },
  no: { label: "No Hire", color: "#991B1B", bg: "#FEE2E2" },
  strong_no: { label: "Strong No", color: "#7F1D1D", bg: "#FEE2E2" },
};

function matrixSC(score) {
  const sc = Number(score);
  if (Number.isNaN(sc)) return MATRIX_T.t3;
  if (sc >= 7) return MATRIX_T.green;
  if (sc >= 5) return MATRIX_T.amber;
  return MATRIX_T.red;
}

function formatSessionDurationForMatrix(s) {
  const status = String(s.status || "").toLowerCase();
  const sec = s.duration_seconds;
  if (status === "in_progress" && (sec == null || Number(sec) === 0)) return "—";
  if (sec != null && Number(sec) > 0) return String(Math.round((Number(sec) / 60) * 10) / 10);
  return "0";
}

function TrustPerformanceMatrix({ sessions }) {
  const T = MATRIX_T;
  const [hoveredDot, setHoveredDot] = useState(null);
  const [activeQuadrant, setActiveQuadrant] = useState(null);

  const getRiskScore = (violations, duration) => {
    const mins = duration === "—" ? 0.5 : parseFloat(duration) || 1;
    const rate = violations / mins;
    if (rate === 0) return 0.5;
    if (rate < 1) return 2;
    if (rate < 3) return 4;
    if (rate < 6) return 6.5;
    if (rate < 10) return 8.5;
    return 9.5;
  };

  const dots = (sessions || []).map((s) => {
    const violations = Number(s.violation_count ?? s.total_violations ?? s.violations ?? 0);
    const duration = formatSessionDurationForMatrix(s);
    return {
      id: s.id,
      name: s.candidate_name || "—",
      campaign: s.campaign_name || "—",
      score: Number(s.overall_score ?? s.score ?? 0),
      riskScore: getRiskScore(violations, duration),
      violations,
      rec: s.hiring_recommendation ?? s.recommendation ?? "no",
    };
  });

  const getQuadrant = (score, risk) => {
    const highScore = score >= 6;
    const highRisk = risk >= 5;
    if (highScore && !highRisk) return { label: "Genuine Talent", color: T.green, bg: T.greenLight };
    if (highScore && highRisk) return { label: "Smart Cheater", color: T.red, bg: T.redLight };
    if (!highScore && !highRisk) return { label: "Honest but Weak", color: T.amber, bg: T.amberLight };
    return { label: "Definite No", color: "#7F1D1D", bg: T.redLight };
  };

  const summary = {
    "Genuine Talent": dots.filter((d) => d.score >= 6 && d.riskScore < 5).length,
    "Smart Cheater": dots.filter((d) => d.score >= 6 && d.riskScore >= 5).length,
    "Honest but Weak": dots.filter((d) => d.score < 6 && d.riskScore < 5).length,
    "Definite No": dots.filter((d) => d.score < 6 && d.riskScore >= 5).length,
  };

  const quadrantStripMeta = {
    "Genuine Talent": { color: T.green, bg: T.greenLight },
    "Smart Cheater": { color: T.red, bg: T.redLight },
    "Honest but Weak": { color: T.amber, bg: T.amberLight },
    "Definite No": { color: "#7F1D1D", bg: T.redLight },
  };

  const filteredDots = activeQuadrant
    ? dots.filter((d) => getQuadrant(d.score, d.riskScore).label === activeQuadrant)
    : dots;

  const W = 420;
  const H = 280;
  const PAD = { top: 20, right: 20, bottom: 36, left: 36 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const xPos = (score) => PAD.left + (score / 10) * plotW;
  const yPos = (risk) => PAD.top + ((10 - risk) / 10) * plotH;
  const midX = xPos(6);
  const midY = yPos(5);

  const hovered = hoveredDot !== null ? dots.find((d) => d.id === hoveredDot) : null;

  return (
    <div style={{ background: T.card, border: `1px solid ${T.bdr}`, borderRadius: 14, padding: "20px" }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: T.t1,
          marginBottom: 12,
          textTransform: "uppercase",
          letterSpacing: 0.8,
        }}
      >
        Trust vs Performance Matrix
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {Object.entries(summary).map(([label, count]) => {
          const q = quadrantStripMeta[label] || { color: T.t3, bg: T.card };
          const isActive = activeQuadrant === label;
          return (
            <button
              key={label}
              type="button"
              onClick={() => setActiveQuadrant(isActive ? null : label)}
              style={{
                fontSize: 10,
                padding: "4px 10px",
                borderRadius: 20,
                border: `1px solid ${q.color}40`,
                background: isActive ? q.bg : "transparent",
                color: q.color,
                cursor: "pointer",
                fontFamily: "inherit",
                fontWeight: 600,
                transition: "all .15s",
              }}
            >
              {label}: <strong>{count}</strong>
            </button>
          );
        })}
        {activeQuadrant && (
          <button
            type="button"
            onClick={() => setActiveQuadrant(null)}
            style={{
              fontSize: 10,
              padding: "4px 10px",
              borderRadius: 20,
              border: `1px solid ${T.bdr}`,
              background: "none",
              color: T.t3,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Clear ✕
          </button>
        )}
      </div>

      <div style={{ position: "relative" }}>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
          <rect x={midX} y={PAD.top} width={W - PAD.right - midX} height={midY - PAD.top} fill={T.redLight} opacity={0.5} rx={4} />
          <rect x={midX} y={midY} width={W - PAD.right - midX} height={PAD.top + plotH - midY} fill={T.greenLight} opacity={0.5} rx={4} />
          <rect x={PAD.left} y={PAD.top} width={midX - PAD.left} height={midY - PAD.top} fill={T.redLight} opacity={0.3} rx={4} />
          <rect x={PAD.left} y={midY} width={midX - PAD.left} height={PAD.top + plotH - midY} fill={T.amberLight} opacity={0.5} rx={4} />

          <text x={midX + (W - PAD.right - midX) / 2} y={PAD.top + 14} textAnchor="middle" fontSize={9} fill={T.red} fontWeight={600} opacity={0.7}>
            Smart Cheater ⚠
          </text>
          <text x={midX + (W - PAD.right - midX) / 2} y={H - PAD.bottom - 8} textAnchor="middle" fontSize={9} fill={T.green} fontWeight={600} opacity={0.7}>
            Genuine Talent ✓
          </text>
          <text x={PAD.left + (midX - PAD.left) / 2} y={H - PAD.bottom - 8} textAnchor="middle" fontSize={9} fill={T.amber} fontWeight={600} opacity={0.7}>
            Honest but Weak
          </text>
          <text x={PAD.left + (midX - PAD.left) / 2} y={PAD.top + 14} textAnchor="middle" fontSize={9} fill={T.red} fontWeight={600} opacity={0.7}>
            Definite No ✕
          </text>

          <line x1={midX} y1={PAD.top} x2={midX} y2={PAD.top + plotH} stroke={T.bdr2} strokeWidth={1} strokeDasharray="4,3" />
          <line x1={PAD.left} y1={midY} x2={PAD.left + plotW} y2={midY} stroke={T.bdr2} strokeWidth={1} strokeDasharray="4,3" />

          {[0, 2, 4, 6, 8, 10].map((v) => (
            <g key={v}>
              <line x1={xPos(v)} y1={PAD.top + plotH} x2={xPos(v)} y2={PAD.top + plotH + 4} stroke={T.t4} strokeWidth={1} />
              <text x={xPos(v)} y={PAD.top + plotH + 14} textAnchor="middle" fontSize={9} fill={T.t3}>
                {v}
              </text>
            </g>
          ))}
          <text x={PAD.left + plotW / 2} y={H - 2} textAnchor="middle" fontSize={9} fill={T.t3}>
            Score →
          </text>

          {[0, 2.5, 5, 7.5, 10].map((v) => (
            <g key={v}>
              <line x1={PAD.left - 4} y1={yPos(v)} x2={PAD.left} y2={yPos(v)} stroke={T.t4} strokeWidth={1} />
              <text x={PAD.left - 7} y={yPos(v) + 3} textAnchor="end" fontSize={9} fill={T.t3}>
                {v}
              </text>
            </g>
          ))}
          <text x={10} y={PAD.top + plotH / 2} textAnchor="middle" fontSize={9} fill={T.t3} transform={`rotate(-90,10,${PAD.top + plotH / 2})`}>
            Risk →
          </text>

          {filteredDots.map((d) => {
            const q = getQuadrant(d.score, d.riskScore);
            const isHovered = hoveredDot === d.id;
            return (
              <g key={d.id}>
                {isHovered && <circle cx={xPos(d.score)} cy={yPos(d.riskScore)} r={10} fill={q.color} opacity={0.15} />}
                <circle
                  cx={xPos(d.score)}
                  cy={yPos(d.riskScore)}
                  r={isHovered ? 7 : 5}
                  fill={q.color}
                  opacity={isHovered ? 1 : 0.75}
                  stroke="white"
                  strokeWidth={1.5}
                  style={{ cursor: "pointer", transition: "r .15s" }}
                  onMouseEnter={() => setHoveredDot(d.id)}
                  onMouseLeave={() => setHoveredDot(null)}
                />
              </g>
            );
          })}
        </svg>

        {hovered &&
          (() => {
            const q = getQuadrant(hovered.score, hovered.riskScore);
            const b = MATRIX_BADGE[hovered.rec] || MATRIX_BADGE.no;
            return (
              <div
                style={{
                  position: "absolute",
                  top: yPos(hovered.riskScore) - 10,
                  left: Math.min(xPos(hovered.score) + 10, W - 160),
                  background: "white",
                  border: `1px solid ${T.bdr}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  boxShadow: "0 4px 16px rgba(0,0,0,.1)",
                  zIndex: 10,
                  minWidth: 150,
                  pointerEvents: "none",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: T.t0, marginBottom: 3 }}>{hovered.name}</div>
                <div style={{ fontSize: 10, color: T.t2, marginBottom: 6 }}>{hovered.campaign}</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: matrixSC(hovered.score) }}>{Number(hovered.score).toFixed(1)}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: q.bg, color: q.color }}>{q.label}</span>
                </div>
                <div
                  style={{
                    fontSize: 9,
                    marginTop: 5,
                    padding: "2px 7px",
                    borderRadius: 20,
                    background: b.bg,
                    color: b.color,
                    display: "inline-block",
                    fontWeight: 700,
                  }}
                >
                  {b.label}
                </div>
              </div>
            );
          })()}
      </div>

      <div style={{ marginTop: 10, fontSize: 10, color: T.t3, fontStyle: "italic", textAlign: "center" }}>
        High score + high risk may indicate unfair assistance
      </div>
    </div>
  );
}

export default function Analytics() {
  const navigate = useNavigate();
  const [allSessions, setAllSessions] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignSort, setCampaignSort] = useState("name");
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [selectedPositions, setSelectedPositions] = useState(() => new Set());
  const [selectedRecruiters, setSelectedRecruiters] = useState(() => new Set());
  const [positionOpen, setPositionOpen] = useState(false);
  const [recruiterOpen, setRecruiterOpen] = useState(false);
  const positionRef = useRef(null);
  const recruiterRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (USE_DEMO_DATA) {
        setAllSessions(withCurrentRecruiterInDemoSessions(DUMMY_SESSIONS));
        setAllCampaigns(DUMMY_CAMPAIGNS.map((c) => ({ ...c })));
        return;
      }
      const [sessRes, campaignsRes] = await Promise.all([
        sessionAPI.getAllSessions({ scope: 'stats' }),
        campaignAPI.listForStats(undefined).catch(() => ({ data: [] })),
      ]);
      setAllSessions(sessRes.data || []);
      setAllCampaigns(campaignsRes.data || []);
    } catch (err) {
      console.error("Analytics fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!positionOpen) return;
    const handler = (e) => {
      if (positionRef.current && !positionRef.current.contains(e.target)) setPositionOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [positionOpen]);

  useEffect(() => {
    if (!recruiterOpen) return;
    const handler = (e) => {
      if (recruiterRef.current && !recruiterRef.current.contains(e.target)) setRecruiterOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [recruiterOpen]);

  const positionOptions = useMemo(() => {
    const map = new Map();
    (allCampaigns || []).forEach((c) => {
      const id = String(c.id);
      map.set(id, {
        id,
        name: c.name || "Unknown",
        role: c.job_role || "",
        isActive: !!c.is_active,
      });
    });
    (allSessions || []).forEach((s) => {
      const id = String(s.campaign_id || "");
      if (!id || map.has(id)) return;
      map.set(id, {
        id,
        name: s.campaign_name || "Unknown",
        role: s.job_role || "",
        isActive: true,
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allCampaigns, allSessions]);

  const recruiterOptions = useMemo(
    () => buildRecruiterOptions(allSessions),
    [allSessions]
  );

  const filteredSessions = useMemo(() => {
    return (allSessions || []).filter((s) => {
      if (selectedPositions.size > 0) {
        const cid = String(s.campaign_id || "");
        if (!selectedPositions.has(cid)) return false;
      }
      if (selectedRecruiters.size > 0) {
        const rec = (s.recruiter_name || "").trim();
        if (!selectedRecruiters.has(rec)) return false;
      }
      return true;
    });
  }, [allSessions, selectedPositions, selectedRecruiters]);

  const sessions = filteredSessions;

  const togglePosition = (id) => {
    setSelectedPositions((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleRecruiter = (name) => {
    setSelectedRecruiters((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const removePositionFilter = (id) => {
    setSelectedPositions((prev) => {
      const next = new Set(prev);
      next.delete(String(id));
      return next;
    });
  };

  const removeRecruiterFilter = (name) => {
    setSelectedRecruiters((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const positionLabel =
    selectedPositions.size === 0
      ? "All roles"
      : selectedPositions.size === 1
        ? positionOptions.find((p) => selectedPositions.has(p.id))?.name || "1 selected"
        : `${selectedPositions.size} roles`;

  const recruiterLabel =
    selectedRecruiters.size === 0
      ? "All Users"
      : selectedRecruiters.size === 1
        ? Array.from(selectedRecruiters)[0]
        : `${selectedRecruiters.size} users`;

  const filterInp = {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px",
    borderRadius: 8,
    border: "1.5px solid #E2E8F0",
    background: "#fff",
    fontSize: 13,
    fontFamily: "inherit",
    color: "#0F172A",
    outline: "none",
  };

  const filterLabelStyle = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color: "#64748B",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  const chipBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 20,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 600,
    maxWidth: 280,
  };

  const chipClearBtn = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    fontSize: 11,
    lineHeight: 1,
    fontFamily: "inherit",
  };

  const hasFilters = selectedPositions.size > 0 || selectedRecruiters.size > 0;

  const metrics = useMemo(() => {
    const total = sessions.length;
    const completed = sessions.filter((s) => (s.status ?? "") === "completed").length;
    const strongYes = sessions.filter((c) => getSessionRecommendation(c).key === "strong_yes").length;
    const yes = sessions.filter((c) => getSessionRecommendation(c).key === "yes").length;
    const no = sessions.filter((c) => getSessionRecommendation(c).key === "no").length;
    const recommended = strongYes + yes;
    const highViol = sessions.filter((s) => (s.violation_count ?? s.total_violations ?? s.violations ?? 0) > 10).length;

    const scores = sessions
      .map((c) => c.overall_score ?? c.score ?? 0)
      .filter((s) => Number(s) > 0)
      .map(Number);
    const avgScore = scores.length
      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
      : "0.0";

    const dist = [
      { label: "9-10", min: 9, max: 10.01, color: "#10B981" },
      { label: "7-8", min: 7, max: 9, color: "#10B981" },
      { label: "5-6", min: 5, max: 7, color: "#F59E0B" },
      { label: "3-4", min: 3, max: 5, color: "#F97316" },
      { label: "0-2", min: 0, max: 3, color: "#EF4444" },
    ].map((b) => ({
      ...b,
      count: scores.filter((s) => s >= b.min && s < b.max).length,
    }));
    const maxDist = Math.max(...dist.map((d) => d.count), 1);

    const funnel = [
      { label: "Candidates Evaluated", value: total, color: C.brand, pct: 100 },
      { label: "Completed Interview", value: completed, color: C.blue, pct: total ? Math.round((completed / total) * 100) : 0 },
      { label: "Recommended", value: recommended, color: C.green, pct: total ? Math.round((recommended / total) * 100) : 0 },
      { label: "Strong Hire", value: strongYes, color: C.purple, pct: total ? Math.round((strongYes / total) * 100) : 0 },
    ];

    return {
      total,
      completed,
      strongYes,
      yes,
      no,
      recommended,
      highViol,
      avgScore,
      dist,
      maxDist,
      funnel,
    };
  }, [sessions]);

  const campaignTableRows = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    const grouped = {};
    sessions.forEach((s) => {
      const name = s.campaign_name || "Unknown";
      if (!grouped[name]) {
        grouped[name] = {
          id: s.campaign_id || name,
          name,
          role: s.job_role || "—",
          sessions: [],
        };
      }
      grouped[name].sessions.push(s);
    });

    return Object.values(grouped).map((g) => {
      const total = g.sessions.length;
      const completed = g.sessions.filter((s) => s.status === "completed").length;
      const scores = g.sessions.map((s) => parseFloat(s.overall_score) || 0);
      const avgScore = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      const hired = g.sessions.filter((s) => {
        const k = getSessionRecommendation(s).key;
        return k === "strong_yes" || k === "yes";
      }).length;
      const flagged = g.sessions.filter((s) => (s.total_violations || 0) > 10).length;
      const risks = g.sessions.map((s) => {
        const mins = s.duration_seconds ? s.duration_seconds / 60 : 1;
        const rate = (s.total_violations || 0) / mins;
        return rate < 1 ? 0.5 : rate < 3 ? 2 : rate < 6 ? 4.5 : rate < 10 ? 7 : 9.5;
      });
      const avgRisk = risks.length ? risks.reduce((a, b) => a + b, 0) / risks.length : 0;
      const lastDate = g.sessions
        .map((s) => (s.started_at ? new Date(s.started_at) : null))
        .filter(Boolean)
        .sort((a, b) => b - a)[0];
      const lastActivity = lastDate
        ? (() => {
            const hrs = Math.round((Date.now() - lastDate.getTime()) / 3600000);
            if (hrs < 1) return "Just now";
            if (hrs < 24) return `${hrs}h ago`;
            return `${Math.round(hrs / 24)}d ago`;
          })()
        : "—";

      return {
        id: g.id,
        name: g.name,
        role: g.role,
        evaluated: total,
        completionRate: total ? Math.round((completed / total) * 100) : 0,
        avgScore,
        hireRate: total ? Math.round((hired / total) * 100) : 0,
        fraudRate: total ? Math.round((flagged / total) * 100) : 0,
        avgRisk,
        lastActivity,
      };
    });
  }, [sessions]);

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 13, color: "#94A3B8" }}>Loading analytics...</div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "28px 32px" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#0F172A" }}>Analytics</div>
          {Array.from(selectedPositions).map((id) => {
            const pos = positionOptions.find((p) => p.id === String(id));
            return (
              <span
                key={`pos-${id}`}
                style={{
                  ...chipBase,
                  background: "rgba(59,130,246,0.12)",
                  border: "1px solid rgba(59,130,246,0.25)",
                  color: "#2563EB",
                }}
                title={pos?.name || id}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {pos?.name || `Position ${id}`}
                </span>
                <button type="button" onClick={() => removePositionFilter(id)} title="Remove position filter" style={{ ...chipClearBtn, color: "#2563EB" }}>
                  ✕
                </button>
              </span>
            );
          })}
          {Array.from(selectedRecruiters).map((name) => (
            <span
              key={`rec-${name}`}
              style={{
                ...chipBase,
                background: "rgba(16,185,129,0.12)",
                border: "1px solid rgba(16,185,129,0.3)",
                color: "#059669",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
              <button type="button" onClick={() => removeRecruiterFilter(name)} title="Remove user filter" style={{ ...chipClearBtn, color: "#059669" }}>
                ✕
              </button>
            </span>
          ))}
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setSelectedPositions(new Set());
                setSelectedRecruiters(new Set());
              }}
              title="Clear all filters"
              style={{
                ...chipBase,
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#DC2626",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Clear
            </button>
          )}
          {!hasFilters && (
            <span style={{ background: "#F1F5F9", border: "1px solid #E2E8F0", color: "#64748B", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>
              All roles
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 14 }}>
          Hiring performance insights{hasFilters ? " for selected filters" : " across all positions"}.
        </div>

        <div
          style={{
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 20,
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 200px", minWidth: 170 }} ref={positionRef}>
              <div style={filterLabelStyle}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                Role / Position
              </div>
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => { setPositionOpen((o) => !o); setRecruiterOpen(false); }}
                  style={{
                    ...filterInp,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    textAlign: "left",
                    borderColor: selectedPositions.size > 0 ? "rgba(37,99,235,0.5)" : "#E2E8F0",
                    background: selectedPositions.size > 0 ? "rgba(37,99,235,0.07)" : "#fff",
                  }}
                >
                  <span style={{ fontSize: 13, color: selectedPositions.size > 0 ? "#2563EB" : "#64748B", fontWeight: selectedPositions.size > 0 ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {positionLabel}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ flexShrink: 0, transform: positionOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s" }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {positionOpen && (
                  <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 40, background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: "100%", maxWidth: 340, maxHeight: 240, overflowY: "auto" }}>
                    {selectedPositions.size > 0 && (
                      <button type="button" onClick={() => setSelectedPositions(new Set())} style={{ display: "block", width: "100%", padding: "8px 14px", background: "transparent", border: "none", borderBottom: "1px solid #E2E8F0", cursor: "pointer", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#EF4444", fontFamily: "inherit" }}>
                        ✕ Clear selection
                      </button>
                    )}
                    {positionOptions.length === 0 ? (
                      <div style={{ padding: "12px 14px", color: "#94A3B8", fontSize: 12 }}>No positions found</div>
                    ) : positionOptions.map((p) => {
                      const checked = selectedPositions.has(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => togglePosition(p.id)}
                          style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 14px", background: checked ? "rgba(37,99,235,0.08)" : "transparent", border: "none", borderBottom: "1px solid #F1F5F9", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
                        >
                          <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? "#2563EB" : "#CBD5E1"}`, background: checked ? "#2563EB" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {checked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#0F172A", lineHeight: 1.3 }}>{p.name}</div>
                            {p.role ? <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>{p.role}</div> : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={{ flex: "1 1 200px", minWidth: 170 }} ref={recruiterRef}>
              <div style={filterLabelStyle}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                User (SSO)
              </div>
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => { setRecruiterOpen((o) => !o); setPositionOpen(false); }}
                  style={{
                    ...filterInp,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    textAlign: "left",
                    borderColor: selectedRecruiters.size > 0 ? "rgba(16,185,129,0.5)" : "#E2E8F0",
                    background: selectedRecruiters.size > 0 ? "rgba(16,185,129,0.07)" : "#fff",
                  }}
                >
                  <span style={{ fontSize: 13, color: selectedRecruiters.size > 0 ? "#059669" : "#64748B", fontWeight: selectedRecruiters.size > 0 ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {recruiterLabel}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ flexShrink: 0, transform: recruiterOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s" }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {recruiterOpen && (
                  <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 40, background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: "100%", maxWidth: 340, maxHeight: 240, overflowY: "auto" }}>
                    {selectedRecruiters.size > 0 && (
                      <button type="button" onClick={() => setSelectedRecruiters(new Set())} style={{ display: "block", width: "100%", padding: "8px 14px", background: "transparent", border: "none", borderBottom: "1px solid #E2E8F0", cursor: "pointer", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#EF4444", fontFamily: "inherit" }}>
                        ✕ Clear selection
                      </button>
                    )}
                    {recruiterOptions.length === 0 ? (
                      <div style={{ padding: "12px 14px", color: "#94A3B8", fontSize: 12 }}>No recruiters found</div>
                    ) : recruiterOptions.map((name) => {
                      const checked = selectedRecruiters.has(name);
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => toggleRecruiter(name)}
                          style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 14px", background: checked ? "rgba(16,185,129,0.08)" : "transparent", border: "none", borderBottom: "1px solid #F1F5F9", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
                        >
                          <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? "#059669" : "#CBD5E1"}`, background: checked ? "#059669" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {checked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#0F172A" }}>{name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {hasFilters && (
              <button
                type="button"
                onClick={() => { setSelectedPositions(new Set()); setSelectedRecruiters(new Set()); }}
                style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", fontSize: 12, fontWeight: 600, color: "#64748B", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          {
            label: "Total Candidates",
            value: metrics.total,
            sub: "",
            color: "#F97316",
            icon: (
              <AnalyticsKpiIcon color="#EA580C">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </AnalyticsKpiIcon>
            ),
          },
          {
            label: "Hire Rate",
            value: `${metrics.recommended} / ${metrics.total}`,
            sub: "",
            color: "#10B981",
            icon: (
              <AnalyticsKpiIcon color="#059669">
                <polyline points="20 6 9 17 4 12" />
              </AnalyticsKpiIcon>
            ),
          },
          {
            label: "Avg Score",
            value: metrics.avgScore,
            sub: "",
            color: "#8B5CF6",
            icon: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="16" height="16">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            ),
          },
          {
            label: "High Violations",
            value: metrics.highViol,
            sub: "",
            color: "#EF4444",
            icon: (
              <AnalyticsKpiIcon color="#DC2626">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </AnalyticsKpiIcon>
            ),
          },
        ].map((s) => (
          <div
            key={s.label}
            style={{ background: "white", borderRadius: 12, padding: "18px 20px", border: "1px solid #E2E8F0", borderLeft: `3px solid ${s.color}`, transition: "all .2s", cursor: "default" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.t2 }}>{s.label}</div>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: s.color + "18",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {s.icon}
              </div>
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: "#0F172A", lineHeight: 1, marginBottom: s.sub ? 4 : 0 }}>{s.value}</div>
            {s.sub ? (
              <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 500 }}>{s.sub}</div>
            ) : null}
          </div>
        ))}
      </div>

      {/* Score distribution */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginBottom: 16 }}>
        <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 14, padding: "20px" }}>
          {(() => {
            const bands = [
              { label: "0–2", min: 0, max: 2, color: "#EF4444" },
              { label: "3–4", min: 3, max: 4, color: "#F97316" },
              { label: "5–6", min: 5, max: 6, color: "#F59E0B" },
              { label: "7–8", min: 7, max: 8, color: "#84CC16" },
              { label: "9–10", min: 9, max: 10, color: "#10B981" },
            ];

            const counts = bands.map((b) => ({
              ...b,
              count: sessions.filter((s) => {
                const sc = parseFloat(s.overall_score) || 0;
                return sc >= b.min && sc <= b.max;
              }).length,
            }));

            const total = sessions.length || 1;
            const maxCount = Math.max(...counts.map((c) => c.count), 1);
            const avgScore = sessions.length
              ? (sessions.reduce((a, s) => a + (parseFloat(s.overall_score) || 0), 0) / sessions.length).toFixed(1)
              : "—";
            const topBand = counts[4];
            const topPct = Math.round((topBand.count / total) * 100);

            const CHART_H = 140;
            const BAR_W = 52;
            const GAP = 14;
            const TOTAL_W = bands.length * (BAR_W + GAP) - GAP;

            return (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Score distribution</div>
                  <div style={{ display: "flex", gap: 16 }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 3 }}>Avg score</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#6366F1", letterSpacing: "-1px" }}>{avgScore}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 3 }}>Scored 8+</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#10B981", letterSpacing: "-1px" }}>{topPct}%</div>
                    </div>
                  </div>
                </div>

                <svg width="100%" viewBox={`0 0 ${TOTAL_W} ${CHART_H + 52}`} style={{ overflow: "visible", maxWidth: 420 }}>
                  {[0, 25, 50, 75, 100].map((pct) => {
                    const y = CHART_H - (pct / 100) * CHART_H;
                    return (
                      <g key={pct}>
                        <line x1={0} y1={y} x2={TOTAL_W} y2={y} stroke="#F1F5F9" strokeWidth={1} />
                        <text x={-4} y={y + 4} textAnchor="end" fontSize={8} fill="#CBD5E1">
                          {Math.round((pct / 100) * maxCount)}
                        </text>
                      </g>
                    );
                  })}

                  {counts.map((c, i) => {
                    const barH = maxCount > 0 ? Math.max((c.count / maxCount) * CHART_H, c.count > 0 ? 6 : 0) : 0;
                    const x = i * (BAR_W + GAP);
                    const y = CHART_H - barH;
                    const pct = Math.round((c.count / total) * 100);

                    return (
                      <g key={c.label}>
                        <rect x={x + 2} y={y + 2} width={BAR_W} height={barH} fill={c.color} opacity={0.12} rx={6} />
                        <rect x={x} y={y} width={BAR_W} height={barH} fill={c.color} opacity={0.85} rx={6} />

                        {c.count > 0 && (
                          <text x={x + BAR_W / 2} y={y - 18} textAnchor="middle" fontSize={14} fontWeight={800} fill={c.color}>
                            {c.count}
                          </text>
                        )}

                        {c.count > 0 && (
                          <text x={x + BAR_W / 2} y={y - 6} textAnchor="middle" fontSize={9} fill="#94A3B8" fontWeight={500}>
                            {pct}%
                          </text>
                        )}

                        <text x={x + BAR_W / 2} y={CHART_H + 16} textAnchor="middle" fontSize={11} fontWeight={600} fill="#64748B">
                          {c.label}
                        </text>

                        {c.count === 0 && (
                          <text x={x + BAR_W / 2} y={CHART_H - 6} textAnchor="middle" fontSize={9} fill="#CBD5E1">
                            0
                          </text>
                        )}
                      </g>
                    );
                  })}

                  {sessions.length > 0 &&
                    (() => {
                      const avg = parseFloat(avgScore) || 0;
                      const bandIdx = Math.min(Math.floor(avg / 2), 4);
                      const withinBand = (avg % 2) / 2;
                      const x = bandIdx * (BAR_W + GAP) + withinBand * (BAR_W + GAP);
                      return (
                        <g>
                          <line x1={x} y1={0} x2={x} y2={CHART_H} stroke="#6366F1" strokeWidth={1.5} strokeDasharray="5,3" opacity={0.6} />
                          <rect x={x - 22} y={-2} width={44} height={14} rx={7} fill="#6366F1" opacity={0.9} />
                          <text x={x} y={8} textAnchor="middle" fontSize={8} fontWeight={700} fill="white">
                            avg {avgScore}
                          </text>
                        </g>
                      );
                    })()}
                </svg>

                {topPct < 15 && sessions.length >= 5 && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "7px 12px",
                      borderRadius: 8,
                      background: "#FFFBEB",
                      border: "1px solid #FDE68A",
                      fontSize: 10,
                      fontWeight: 500,
                      color: "#D97706",
                    }}
                  >
                    ⚠ Only {topPct}% scored 8+ — candidate pool quality may need review
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Violation breakdown</div>
            <button type="button" style={{ fontSize: 10, color: "#DC2626", background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>
              Review flagged →
            </button>
          </div>

          {(() => {
            const total = sessions.length || 1;

            const violationTypes = [
              { key: "looking_away", label: "Looking Away", icon: "👁", color: "#EF4444", prevPct: 12 },
              { key: "tab_switch", label: "Tab Switch", icon: "🔀", color: "#F97316", prevPct: 8 },
              { key: "multiple_voices", label: "Multiple Voices", icon: "🎙", color: "#8B5CF6", prevPct: 5 },
              { key: "object_detected", label: "Object Detected", icon: "📱", color: "#0EA5E9", prevPct: 3 },
              { key: "lip_sync_mismatch", label: "Lip Sync Mismatch", icon: "🔇", color: "#EC4899", prevPct: 2 },
              { key: "multiple_faces", label: "Multiple Faces", icon: "👥", color: "#DC2626", prevPct: 4 },
            ];

            const counts = violationTypes.map((vt) => {
              const affected = sessions.filter((s) => {
                if (s[vt.key] !== undefined) return s[vt.key] > 0;
                return false;
              }).length;
              return { ...vt, affected };
            });

            const hasSpecificFields = sessions.some((s) => s.looking_away !== undefined);
            const fallbackCounts = !hasSpecificFields
              ? violationTypes.map((vt, i) => {
                  const weights = [0.35, 0.25, 0.15, 0.12, 0.08, 0.05];
                  const flagged = sessions.filter((s) => (s.total_violations || 0) > 0).length;
                  return { ...vt, affected: Math.round(flagged * weights[i]) };
                })
              : counts;

            const maxAffected = Math.max(...fallbackCounts.map((v) => v.affected), 1);

            return (
              <>
                {fallbackCounts.map((v) => {
                  const pct = Math.round((v.affected / total) * 100);
                  const trend = pct - v.prevPct;
                  const barWidth = Math.round((v.affected / maxAffected) * 100);

                  return (
                    <div
                      key={v.key}
                      style={{ marginBottom: 14, cursor: "pointer", padding: "6px 8px", borderRadius: 8, transition: "background .12s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <span style={{ fontSize: 13 }}>{v.icon}</span>
                          <span style={{ fontSize: 11, fontWeight: 500, color: "#475569" }}>{v.label}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, color: trend > 0 ? "#DC2626" : trend < 0 ? "#059669" : "#94A3B8" }}>
                            {trend > 0 ? `↑ +${trend}%` : trend < 0 ? `↓ ${trend}%` : "→ 0%"}
                            <span style={{ color: "#94A3B8", fontWeight: 400 }}> vs last</span>
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#0F172A" }}>{v.affected}</span>
                          <span style={{ fontSize: 10, color: "#94A3B8", minWidth: 32, textAlign: "right" }}>{pct}%</span>
                        </div>
                      </div>

                      <div style={{ height: 6, background: "#F1F5F9", borderRadius: 3, overflow: "hidden" }}>
                        <div
                          style={{
                            height: "100%",
                            width: `${barWidth}%`,
                            background: v.color,
                            borderRadius: 3,
                            transition: "width .8s ease",
                            opacity: 0.85,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}

                <div
                  style={{
                    marginTop: 8,
                    fontSize: 10,
                    color: "#94A3B8",
                    borderTop: "1px solid #F1F5F9",
                    paddingTop: 10,
                  }}
                >
                  % = share of {total} evaluated sessions affected by each violation type
                </div>
              </>
            );
          })()}
        </div>

        <TrustPerformanceMatrix sessions={sessions} />
      </div>

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden", marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #E2E8F0", background: "#F8FAFC" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Position comparison</div>
            <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>Click any row to filter the matrix and funnel above</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              placeholder="Search positions..."
              value={campaignSearch}
              onChange={(e) => setCampaignSearch(e.target.value)}
              style={{ fontSize: 11, padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", outline: "none", color: "#0F172A", fontFamily: "inherit", width: 180 }}
            />
            <select
              value={campaignSort}
              onChange={(e) => setCampaignSort(e.target.value)}
              style={{ fontSize: 11, padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", outline: "none", color: "#0F172A", fontFamily: "inherit", cursor: "pointer" }}
            >
              <option value="name">Sort: Name</option>
              <option value="score">Sort: Avg Score</option>
              <option value="hireRate">Sort: Hire Rate</option>
              <option value="fraudRate">Sort: Fraud Rate</option>
              <option value="completion">Sort: Completion</option>
            </select>
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
              {[
                { label: "Position", w: "18%" },
                { label: "Evaluated", w: "9%" },
                { label: "Completion", w: "13%" },
                { label: "Avg Score", w: "10%" },
                { label: "Hire Rate", w: "13%" },
                { label: "Fraud Rate", w: "13%" },
                { label: "Avg Risk", w: "10%" },
                { label: "Last Activity", w: "12%" },
                { label: "", w: "8%" },
              ].map((h) => (
                <th key={h.label} style={{ padding: "9px 16px", fontSize: 9, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700, textAlign: "left", width: h.w }}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {campaignTableRows
              .filter((r) => r.name.toLowerCase().includes(campaignSearch.toLowerCase()))
              .sort((a, b) => {
                if (campaignSort === "score") return b.avgScore - a.avgScore;
                if (campaignSort === "hireRate") return b.hireRate - a.hireRate;
                if (campaignSort === "fraudRate") return b.fraudRate - a.fraudRate;
                if (campaignSort === "completion") return b.completionRate - a.completionRate;
                return a.name.localeCompare(b.name);
              })
              .map((row, i) => (
                <tr
                  key={i}
                  onClick={() => setActiveCampaign(activeCampaign === row.name ? null : row.name)}
                  style={{
                    borderBottom: "1px solid #F1F5F9",
                    cursor: "pointer",
                    background: activeCampaign === row.name ? "#F5F3FF" : "transparent",
                    transition: "background .12s",
                  }}
                >
                  <td style={{ padding: "10px 16px" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>{row.name}</div>
                    <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>{row.role}</div>
                  </td>

                  <td style={{ padding: "10px 16px" }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: "#0F172A" }}>{row.evaluated}</span>
                  </td>

                  <td style={{ padding: "10px 16px", minWidth: 100 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 8, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
                        <div
                          style={{
                            height: "100%",
                            width: `${row.completionRate}%`,
                            background: row.completionRate >= 80 ? "#6366F1" : row.completionRate >= 50 ? "#8B5CF6" : "#CBD5E1",
                            borderRadius: 4,
                            transition: "width .6s ease",
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#475569", flexShrink: 0 }}>{row.completionRate}%</span>
                    </div>
                  </td>

                  <td style={{ padding: "10px 16px" }}>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 44,
                        height: 28,
                        borderRadius: 8,
                        fontWeight: 800,
                        fontSize: 14,
                        background: row.avgScore >= 7 ? "#ECFDF5" : row.avgScore >= 5 ? "#FFFBEB" : "#FEF2F2",
                        color: row.avgScore >= 7 ? "#059669" : row.avgScore >= 5 ? "#D97706" : "#DC2626",
                      }}
                    >
                      {row.avgScore.toFixed(1)}
                    </div>
                  </td>

                  <td style={{ padding: "10px 16px", minWidth: 110 }}>
                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        background: row.hireRate >= 30 ? "#ECFDF5" : row.hireRate >= 10 ? "#F0FDF4" : "#F8FAFC",
                        border: `1px solid ${row.hireRate >= 30 ? "#86EFAC" : row.hireRate >= 10 ? "#BBF7D0" : "#E2E8F0"}`,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: row.hireRate >= 30 ? "#059669" : row.hireRate >= 10 ? "#10B981" : "#94A3B8" }}>{row.hireRate}%</span>
                      </div>
                      <div style={{ height: 5, background: "rgba(0,0,0,.06)", borderRadius: 3, overflow: "hidden" }}>
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.min(row.hireRate, 100)}%`,
                            background: row.hireRate >= 30 ? "#059669" : row.hireRate >= 10 ? "#10B981" : "#D1D5DB",
                            borderRadius: 3,
                          }}
                        />
                      </div>
                    </div>
                  </td>

                  <td style={{ padding: "10px 16px", minWidth: 110 }}>
                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        background: row.fraudRate > 20 ? "#FEF2F2" : row.fraudRate > 5 ? "#FFFBEB" : "#F8FAFC",
                        border: `1px solid ${row.fraudRate > 20 ? "#FECACA" : row.fraudRate > 5 ? "#FDE68A" : "#E2E8F0"}`,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: row.fraudRate > 20 ? "#DC2626" : row.fraudRate > 5 ? "#D97706" : "#94A3B8" }}>{row.fraudRate}%</span>
                      </div>
                      <div style={{ height: 5, background: "rgba(0,0,0,.06)", borderRadius: 3, overflow: "hidden" }}>
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.min(row.fraudRate, 100)}%`,
                            background: row.fraudRate > 20 ? "#EF4444" : row.fraudRate > 5 ? "#F59E0B" : "#D1D5DB",
                            borderRadius: 3,
                          }}
                        />
                      </div>
                    </div>
                  </td>

                  <td style={{ padding: "10px 16px" }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "3px 10px",
                        borderRadius: 20,
                        background: row.avgRisk <= 2 ? "#ECFDF5" : row.avgRisk <= 5 ? "#FFFBEB" : "#FEF2F2",
                        color: row.avgRisk <= 2 ? "#059669" : row.avgRisk <= 5 ? "#D97706" : "#DC2626",
                      }}
                    >
                      {row.avgRisk.toFixed(1)}
                    </span>
                  </td>

                  <td style={{ padding: "10px 16px", fontSize: 11, color: "#94A3B8" }}>{row.lastActivity}</td>

                  <td style={{ padding: "10px 16px" }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/campaigns/${row.id}`);
                      }}
                      style={{
                        fontSize: 10,
                        padding: "5px 12px",
                        borderRadius: 7,
                        border: "1px solid #6366F1",
                        color: "#6366F1",
                        background: "transparent",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontWeight: 600,
                      }}
                    >
                      View →
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {campaignTableRows.length === 0 && (
          <div style={{ padding: "40px", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>
            No position data available yet.
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Efficiency metrics</div>
            <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#FEF3C7", color: "#92400E" }}>Sample data</span>
          </div>

          {[
            { label: "Time to Invite", desc: "Added → Invited", avg: "2.1d", median: "1.8d", sla: "1d", ok: false },
            { label: "Time to Start", desc: "Invited → Started", avg: "1.4d", median: "1.2d", sla: "2d", ok: true },
            { label: "Avg Session Duration", desc: "Interview length", avg: "18m", median: "16m", sla: "30m", ok: true },
            { label: "Review Turnaround", desc: "Completed → Reviewed", avg: "3.2d", median: "2.8d", sla: "1d", ok: false },
          ].map((m, i) => (
            <div
              key={i}
              style={{
                padding: "12px 14px",
                borderRadius: 10,
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                marginBottom: 10,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#0F172A" }}>{m.label}</div>
                  <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>{m.desc}</div>
                </div>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 20,
                    background: m.ok ? "#ECFDF5" : "#FEF2F2",
                    color: m.ok ? "#065F46" : "#991B1B",
                  }}
                >
                  SLA: {m.sla} {m.ok ? "✓" : "✗"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 2 }}>Avg</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: m.ok ? "#059669" : "#DC2626", letterSpacing: "-.5px" }}>{m.avg}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 2 }}>Median</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#475569", letterSpacing: "-.5px" }}>{m.median}</div>
                </div>
                <div style={{ marginLeft: "auto", textAlign: "right" }}>
                  <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 2 }}>Actual vs SLA</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: m.ok ? "#059669" : "#DC2626" }}>{m.ok ? `${m.avg} ≤ ${m.sla}` : `${m.avg} > ${m.sla}`}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "20px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", marginBottom: 18 }}>Recommendation breakdown</div>

          {(() => {
            const total = sessions.length || 1;
            const strong = sessions.filter((s) => getSessionRecommendation(s).key === "strong_yes").length;
            const hire = sessions.filter((s) => getSessionRecommendation(s).key === "yes").length;
            const noHire = sessions.filter((s) => getSessionRecommendation(s).key === "no").length;

            const rows = [
              { label: "Strong Hire", val: strong, color: "#059669", bg: "#ECFDF5", trend: +2 },
              { label: "Hire", val: hire, color: "#D97706", bg: "#FEF3C7", trend: +1 },
              { label: "No Hire", val: noHire, color: "#EF4444", bg: "#FEF2F2", trend: -5 },
            ];

            return (
              <>
                <div style={{ display: "flex", height: 28, borderRadius: 8, overflow: "hidden", marginBottom: 20, gap: 2 }}>
                  {rows.map((r) => {
                    const pct = Math.round((r.val / total) * 100);
                    return pct > 0 ? (
                      <div
                        key={r.label}
                        style={{
                          width: `${pct}%`,
                          background: r.color,
                          transition: "width .8s ease",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 700,
                          color: "white",
                        }}
                      >
                        {pct > 8 ? `${pct}%` : ""}
                      </div>
                    ) : null;
                  })}
                </div>

                {rows.map((r, i) => {
                  const pct = Math.round((r.val / total) * 100);
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: r.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 16,
                          fontWeight: 800,
                          color: r.color,
                          flexShrink: 0,
                        }}
                      >
                        {r.val}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                          <span style={{ color: "#475569", fontWeight: 500 }}>{r.label}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: r.trend > 0 ? "#059669" : "#DC2626",
                              }}
                            >
                              {r.trend > 0 ? `↑ +${r.trend}%` : `↓ ${r.trend}%`}
                            </span>
                            <span style={{ fontWeight: 700, color: r.color }}>{pct}%</span>
                          </div>
                        </div>
                        <div style={{ height: 5, background: "#F1F5F9", borderRadius: 3, overflow: "hidden" }}>
                          <div
                            style={{
                              height: "100%",
                              width: `${pct}%`,
                              background: r.color,
                              borderRadius: 3,
                              transition: "width .8s ease",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div
                  style={{
                    marginTop: 8,
                    padding: "12px 14px",
                    background: "#ECFDF5",
                    borderRadius: 10,
                    border: "1px solid #BBF7D0",
                  }}
                >
                  <div style={{ fontSize: 11, color: "#059669", fontWeight: 600, marginBottom: 2 }}>Overall hire rate</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#059669", letterSpacing: "-1px" }}>{Math.round(((strong + hire) / total) * 100)}%</div>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {(() => {
        const total = sessions.length || 1;
        const hired = sessions.filter((s) => {
          const k = getSessionRecommendation(s).key;
          return k === "strong_yes" || k === "yes";
        }).length;
        const flagged = sessions.filter((s) => (s.total_violations || 0) > 10).length;
        const strongHire = sessions.filter((s) => getSessionRecommendation(s).key === "strong_yes").length;
        const hireRate = Math.round((hired / total) * 100);
        const fraudRate = Math.round((flagged / total) * 100);
        const strongPct = Math.round((strongHire / total) * 100);

        const insights = [
          total < 20
            ? {
                level: "info",
                icon: "ℹ",
                text: "Not enough data to generate reliable insights yet — need 20+ sessions",
                color: "#3B82F6",
                bg: "#EFF6FF",
                border: "#BFDBFE",
              }
            : hireRate < 10
              ? {
                  level: "critical",
                  icon: "🔴",
                  text: `Hire rate is critically low at ${hireRate}% — only ${hired} of ${total} candidates recommended`,
                  color: "#DC2626",
                  bg: "#FEF2F2",
                  border: "#FECACA",
                }
              : {
                  level: "info",
                  icon: "🟢",
                  text: `Hire rate is ${hireRate}% — ${hired} candidates recommended across all positions`,
                  color: "#059669",
                  bg: "#ECFDF5",
                  border: "#BBF7D0",
                },
          fraudRate > 15
            ? {
                level: "critical",
                icon: "🔴",
                text: `High fraud rate detected — ${fraudRate}% of sessions (${flagged}) flagged for excessive violations`,
                color: "#DC2626",
                bg: "#FEF2F2",
                border: "#FECACA",
              }
            : fraudRate > 5
              ? {
                  level: "warning",
                  icon: "🟠",
                  text: `Fraud activity present — ${flagged} sessions flagged (${fraudRate}%). Monitor closely`,
                  color: "#D97706",
                  bg: "#FFFBEB",
                  border: "#FDE68A",
                }
              : {
                  level: "info",
                  icon: "🟢",
                  text: `Fraud levels are low — only ${flagged} session${flagged !== 1 ? "s" : ""} flagged (${fraudRate}%)`,
                  color: "#059669",
                  bg: "#ECFDF5",
                  border: "#BBF7D0",
                },
          strongPct < 5
            ? {
                level: "warning",
                icon: "🟠",
                text: `Only ${strongPct}% of candidates qualify as strong hires — candidate pool quality may need review`,
                color: "#D97706",
                bg: "#FFFBEB",
                border: "#FDE68A",
              }
            : {
                level: "info",
                icon: "🟢",
                text: `${strongPct}% of candidates are strong hires — ${strongHire} standout candidate${strongHire !== 1 ? "s" : ""} identified`,
                color: "#059669",
                bg: "#ECFDF5",
                border: "#BBF7D0",
              },
        ];

        return (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Key insights</div>
              <span style={{ fontSize: 10, color: "#94A3B8" }}>Auto-generated from session data</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
              {insights.map((ins, i) => (
                <div
                  key={i}
                  style={{
                    padding: "14px 16px",
                    borderRadius: 12,
                    background: ins.bg,
                    border: `1px solid ${ins.border}`,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: ins.color, lineHeight: 1.6 }}>
                    {ins.icon} {ins.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
